import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import { DetailsSlide } from './slides/DetailsSlide';
import { ReferencesSlide } from './slides/ReferencesSlide';
import { SummarySlide } from './slides/SummarySlide';
import { SwipeableServiceCardProps, AllocationStatus } from './SwipeableServiceCard.props';
import {
  CardContainer,
  SlideContainer,
  SlideTrack,
  Slide,
  DotsContainer,
  Dot,
} from './SwipeableServiceCard.styles';

const getAllocationStatus = (
  primaryAllocationNode: string | undefined,
  currentUserName: string | undefined
): AllocationStatus => {
  if (!primaryAllocationNode) {
    return 'unallocated';
  }
  if (!currentUserName) {
    return 'otherUser';
  }
  if (primaryAllocationNode.toLowerCase() === currentUserName.toLowerCase()) {
    return 'currentUser';
  }
  return 'otherUser';
};

export const SwipeableServiceCard: React.FC<SwipeableServiceCardProps> = ({
  service,
  currentUserName,
  onClick,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const allocationStatus = getAllocationStatus(service.primaryAllocationNode, currentUserName);
  const zone = service.serviceZone || service.locationZone;
  const totalSlides = 3;
  const SWIPE_THRESHOLD = 80;
  const TAP_THRESHOLD = 15;

  // Handle touch start
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
    setTranslateX(0);
  }, []);

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || !containerRef.current) return;
      const dx = e.touches[0].clientX - startX;
      setTranslateX(dx);
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
        trackRef.current.style.transform = `translateX(${-currentSlide * containerRef.current.offsetWidth + dx}px)`;
      }
    },
    [isDragging, startX, currentSlide]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    if (!isDragging || !containerRef.current) return;
    setIsDragging(false);

    let targetSlide = currentSlide;
    if (Math.abs(translateX) > SWIPE_THRESHOLD) {
      const direction = translateX < 0 ? 1 : -1;
      targetSlide = (currentSlide + direction + totalSlides) % totalSlides;
      setCurrentSlide(targetSlide);
    }

    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      trackRef.current.style.transform = `translateX(${-targetSlide * containerRef.current.offsetWidth}px)`;
    }
    setTranslateX(0);
  }, [isDragging, translateX, currentSlide, totalSlides]);

  // Handle mouse start (for desktop testing)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setStartX(e.clientX);
    setIsDragging(true);
    setTranslateX(0);
    e.preventDefault();
  }, []);

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const dx = e.clientX - startX;
      setTranslateX(dx);
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
        trackRef.current.style.transform = `translateX(${-currentSlide * containerRef.current.offsetWidth + dx}px)`;
      }
    },
    [isDragging, startX, currentSlide]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (!isDragging || !containerRef.current) return;
    setIsDragging(false);

    let targetSlide = currentSlide;
    if (Math.abs(translateX) > SWIPE_THRESHOLD) {
      const direction = translateX < 0 ? 1 : -1;
      targetSlide = (currentSlide + direction + totalSlides) % totalSlides;
      setCurrentSlide(targetSlide);
    }

    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      trackRef.current.style.transform = `translateX(${-targetSlide * containerRef.current.offsetWidth}px)`;
    }
    setTranslateX(0);
  }, [isDragging, translateX, currentSlide, totalSlides]);

  // Update slide position when currentSlide changes
  useEffect(() => {
    if (!containerRef.current || !trackRef.current) return;
    trackRef.current.style.transform = `translateX(${-currentSlide * containerRef.current.offsetWidth}px)`;
  }, [currentSlide]);

  // Handle card click (only if not swiping)
  const handleCardClick = useCallback(() => {
    if (Math.abs(translateX) < TAP_THRESHOLD && onClick) {
      onClick();
    }
  }, [translateX, onClick]);

  // Only mount the active slide and its immediate neighbours for smooth
  // transitions. Avoids rendering all 3 slides * N cards.
  const shouldMount = useMemo(
    () =>
      Array.from({ length: totalSlides }, (_, i) => {
        const diff = Math.abs(currentSlide - i);
        return diff <= 1 || diff === totalSlides - 1;
      }),
    [currentSlide, totalSlides]
  );

  const slides = [
    <SummarySlide
      key="summary"
      service={service}
      allocationStatus={allocationStatus}
      zone={zone}
    />,
    <ReferencesSlide
      key="references"
      service={service}
      allocationStatus={allocationStatus}
      zone={zone}
    />,
    <DetailsSlide key="details" service={service} allocationStatus={allocationStatus} />,
  ];

  return (
    <CardContainer
      allocationStatus={allocationStatus}
      onClick={handleCardClick}
      data-testid="service-card"
      data-allocation={allocationStatus}
    >
      <SlideContainer
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        data-testid="swipe-container"
      >
        <SlideTrack ref={trackRef}>
          {slides.map((slide, i) => (
            <Slide key={i}>{shouldMount[i] ? slide : null}</Slide>
          ))}
        </SlideTrack>
      </SlideContainer>
      <DotsContainer>
        {slides.map((_, i) => (
          <Dot key={i} active={currentSlide === i} data-dot={i + 1} />
        ))}
      </DotsContainer>
    </CardContainer>
  );
};
